import React from "react";
import {
  Box, Grid, Paper, Typography,
} from "@mui/material";
import { Category, AccountBalance } from "@mui/icons-material";

/**
 * Financial summary cards for fund-raise and corpus project types.
 */
const ProjectFinancials = ({ projectInfo, corpusDisbursements, linkedBills, linkedPayments }) => {
  if (!projectInfo) return null;

  if (projectInfo.projectType === "fund-raise") {
    const budget = Number(projectInfo.estimatedBudget) || 0;
    const activeDisbursements = corpusDisbursements.filter((d) => d.status !== "cancelled");
    const totalSpent = activeDisbursements.reduce((s, d) => s + Number(d.amount || 0), 0);
    const totalDuesAmount = [...linkedBills, ...linkedPayments].reduce(
      (s, b) => s + Number(b.amount || 0), 0
    );
    const totalCollected = linkedPayments.reduce((s, p) => s + Number(p.amount || 0), 0);
    const totalOutstanding = linkedBills.reduce((s, b) => s + Number(b.amount || 0), 0);
    const netPosition = totalCollected - totalSpent;
    const isRecurring = !budget;

    return (
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e0e0e0" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Category color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Financial Summary
          </Typography>
        </Box>

        {isRecurring ? (
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={2.4}>
              <StatCard value={totalDuesAmount} label="Total Billed" color="primary.main" />
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <StatCard value={totalCollected} label={`Collected (${linkedPayments.length})`} color="success.main" />
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <StatCard value={totalOutstanding} label={`Outstanding (${linkedBills.length})`} color="warning.main" />
            </Grid>
            <Grid item xs={4} sm={2.4}>
              <StatCard value={totalSpent} label={`Payouts (${activeDisbursements.length})`} color="error.main" />
            </Grid>
            <Grid item xs={4} sm={2.4}>
              <StatCard
                value={netPosition}
                label={netPosition >= 0 ? "Surplus" : "Deficit"}
                color={netPosition >= 0 ? "success.main" : "error.main"}
                bgcolor={netPosition >= 0 ? "success.50" : "error.50"}
              />
            </Grid>
          </Grid>
        ) : (
          (() => {
            const remaining = budget - totalSpent;
            return (
              <Grid container spacing={1.5}>
                <Grid item xs={6} sm={2.4}>
                  <StatCard value={budget} label="Budget" color="secondary.main" />
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <StatCard value={totalSpent} label={`Spent (${activeDisbursements.length})`} color="error.main" />
                </Grid>
                <Grid item xs={4} sm={2.4}>
                  <StatCard
                    value={remaining}
                    label={remaining >= 0 ? "Remaining" : "Over Budget"}
                    color={remaining >= 0 ? "success.main" : "warning.main"}
                  />
                </Grid>
                <Grid item xs={4} sm={2.4}>
                  <StatCard
                    value={totalCollected}
                    label={`Collected (${linkedPayments.length}/${linkedBills.length + linkedPayments.length})`}
                    color="success.main"
                  />
                </Grid>
                <Grid item xs={4} sm={2.4}>
                  <StatCard value={totalOutstanding} label={`Outstanding (${linkedBills.length})`} color="warning.main" />
                </Grid>
              </Grid>
            );
          })()
        )}
      </Paper>
    );
  }

  if (projectInfo.projectType === "corpus") {
    const budget = Number(projectInfo.estimatedBudget) || 0;
    const activeDisbursements = corpusDisbursements.filter((d) => d.status !== "cancelled");
    const totalDebited = activeDisbursements.reduce((s, d) => s + Number(d.amount || 0), 0);
    const remaining = budget - totalDebited;

    return (
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e0e0e0" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <AccountBalance color="secondary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Corpus Fund Summary
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          <Grid item xs={4}>
            <StatCard value={budget > 0 ? budget : null} label="Budget" color="secondary.main" />
          </Grid>
          <Grid item xs={4}>
            <StatCard value={totalDebited} label={`Debited (${activeDisbursements.length})`} color="error.main" />
          </Grid>
          <Grid item xs={4}>
            <StatCard value={remaining} label="Remaining" color={remaining >= 0 ? "success.main" : "error.main"} />
          </Grid>
        </Grid>
      </Paper>
    );
  }

  return null;
};

/** Reusable stat card */
const StatCard = ({ value, label, color, bgcolor = "grey.50" }) => (
  <Box sx={{ textAlign: "center", p: { xs: 1, sm: 1.5 }, borderRadius: 2, bgcolor }}>
    <Typography sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.25rem" } }} color={color}>
      {value === null ? "—" : `₹${value.toLocaleString()}`}
    </Typography>
    <Typography variant="caption" color="text.secondary" noWrap>
      {label}
    </Typography>
  </Box>
);

export default ProjectFinancials;
